import React, { useState } from 'react';
import { FaTruck, FaSpinner, FaEye, FaEyeSlash, FaPlus, FaExternalLinkAlt, FaCopy } from 'react-icons/fa';
import { toast } from 'react-toastify';

// ============================================
// TRACKING LINK FOR ONE LOAD - admin + subbie can add it, everyone can see it
// ============================================
// The subbie's tracking provider (MiX Telematics for Intagrey, etc.) hands over a share link with a
// login. It is NOT something we can pull data from - the link is a web app behind a login (see
// PROJECT-NOTES.md) - so the useful thing is to keep the link and its credentials against the load
// where the office, the subbie and the client can all find them.
//
// Those share links LAPSE (MiX's lasts about 7 days), so the expiry is stored and shown, in red once
// it has passed, so somebody knows to ask for a new one instead of passing on a dead link.
//
// WHO CAN ADD: admin and subbie (canEdit). The client only ever sees it.
//
// Self-contained on purpose: it owns its own form state, so any page can drop it in with a load and
// a save URL.
// ============================================

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }) : '');

function LoadTrackingPanel({ load, saveUrl, canEdit = false, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reveal, setReveal] = useState(false);
  const [form, setForm] = useState({ link: '', username: '', password: '', expires_at: '' });

  const hasAnyway = !!(load?.tracking_link || load?.tracking_username || load?.tracking_password);
  const expired = !!(load?.tracking_expires_at && new Date(load.tracking_expires_at) < new Date());
  const href = load?.tracking_link
    ? (String(load.tracking_link).startsWith('http') ? load.tracking_link : `https://${load.tracking_link}`)
    : null;

  const startEdit = () => {
    setForm({
      link: load?.tracking_link || '',
      username: load?.tracking_username || '',
      password: load?.tracking_password || '',
      expires_at: load?.tracking_expires_at ? String(load.tracking_expires_at).slice(0, 10) : ''
    });
    setEditing(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.link.trim()) { toast.error('Paste the tracking link'); return; }
    setSaving(true);
    try {
      const res = await fetch(saveUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tracking_link: form.link.trim(),
          tracking_username: form.username.trim() || null,
          tracking_password: form.password.trim() || null,
          tracking_expires_at: form.expires_at || null
        })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Could not save the tracking link');
      toast.success('Tracking link saved');
      setEditing(false);
      // Both existing endpoints answer with the updated row as `data` (fleetAllocations.js:236 and
      // subbie-scheduler.js:521); some other routes use `allocation`. Accept either.
      if (onSaved) onSaved(data.allocation || data.data || null);
    } catch (err) {
      toast.error(err.message || 'Could not save the tracking link');
    } finally {
      setSaving(false);
    }
  };

  const copy = (value, label) => {
    if (!value) return;
    navigator.clipboard?.writeText(value);
    toast.success(`${label} copied`);
  };

  return (
    <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#E8ECEF' }}>
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-bold flex items-center gap-2" style={{ color: '#0A1929' }}>
          <FaTruck size={12} style={{ color: '#00A86B' }} /> Live tracking
        </h3>
        {canEdit && !editing && (
          <button
            type="button"
            onClick={startEdit}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium text-white"
            style={{ backgroundColor: '#00A86B' }}
          >
            <FaPlus size={10} /> {hasAnyway ? 'Update tracking link' : 'Add tracking link'}
          </button>
        )}
      </div>

      {editing ? (
        <form onSubmit={save} className="space-y-2 rounded-lg border p-3" style={{ borderColor: '#E8ECEF', backgroundColor: '#FAFAFA' }}>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Tracking link *</label>
            <input
              type="text"
              value={form.link}
              onChange={(e) => setForm({ ...form, link: e.target.value })}
              placeholder="https://za.mixtelematics.com/#/tracking/live-tracking"
              className="w-full px-3 py-2 border rounded-lg text-sm"
              style={{ borderColor: '#E8ECEF' }}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Username</label>
              <input
                type="text"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg text-sm"
                style={{ borderColor: '#E8ECEF' }}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Password</label>
              <input
                type="text"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg text-sm"
                style={{ borderColor: '#E8ECEF' }}
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Link expires (optional)</label>
            <input
              type="date"
              value={form.expires_at}
              onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg text-sm"
              style={{ borderColor: '#E8ECEF' }}
            />
            <p className="text-xs text-gray-400 mt-1">Provider share links usually last about 7 days.</p>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="px-3 py-1.5 rounded-lg text-xs font-medium border hover:bg-gray-50"
              style={{ borderColor: '#E8ECEF' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium text-white disabled:opacity-50"
              style={{ backgroundColor: '#00A86B' }}
            >
              {saving && <FaSpinner className="animate-spin" size={11} />}
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      ) : !hasAnyway ? (
        <p className="text-sm text-gray-400">
          No tracking link on this load yet.{canEdit ? ' Add one so the office and the client can follow the truck.' : ''}
        </p>
      ) : (
        <div className="space-y-1.5">
          {href && (
            <p className="text-sm">
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 font-medium hover:underline"
                style={{ color: '#00A86B' }}
              >
                <FaExternalLinkAlt size={11} /> Open live tracking
              </a>
            </p>
          )}
          {load?.tracking_username && (
            <p className="text-sm text-gray-600 flex items-center gap-2">
              Login: <strong style={{ color: '#0A1929' }}>{load.tracking_username}</strong>
              <button type="button" onClick={() => copy(load.tracking_username, 'Username')} className="text-gray-400 hover:text-navy" title="Copy username"><FaCopy size={11} /></button>
            </p>
          )}
          {load?.tracking_password && (
            <p className="text-sm text-gray-600 flex items-center gap-2">
              Password: <strong style={{ color: '#0A1929' }}>{reveal ? load.tracking_password : '••••••••'}</strong>
              <button type="button" onClick={() => setReveal(!reveal)} className="text-gray-400 hover:text-navy" title={reveal ? 'Hide password' : 'Show password'}>
                {reveal ? <FaEyeSlash size={11} /> : <FaEye size={11} />}
              </button>
              <button type="button" onClick={() => copy(load.tracking_password, 'Password')} className="text-gray-400 hover:text-navy" title="Copy password"><FaCopy size={11} /></button>
            </p>
          )}
          {load?.tracking_expires_at && (
            <p className="text-xs" style={{ color: expired ? '#dc2626' : '#4A5568' }}>
              {expired ? 'Expired on ' : 'Valid until '}<strong>{fmtDate(load.tracking_expires_at)}</strong>
              {expired ? ' - ask the subbie for a fresh link.' : ''}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default LoadTrackingPanel;

